/**
 * Utilidades de imagen en el navegador (sin backend):
 * - comprimir / redimensionar
 * - exportar un recorte circular (o cuadrado) como File JPEG
 */

export function loadImageFromUrl(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
    });
}

export function loadImageFromFile(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = (err) => {
            URL.revokeObjectURL(url);
            reject(err);
        };
        img.src = url;
    });
}

export function canvasToJpegFile(canvas, fileName = 'perfil.jpg', quality = 0.85) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('No se pudo generar la imagen'));
                    return;
                }
                resolve(new File([blob], fileName, { type: 'image/jpeg' }));
            },
            'image/jpeg',
            quality
        );
    });
}

/**
 * Reduce dimensiones/peso antes de editar o subir.
 * maxSide: lado máximo en px. quality: 0-1.
 */
export async function compressImageFile(file, { maxSide = 1600, quality = 0.82 } = {}) {
    const img = await loadImageFromFile(file);
    const longest = Math.max(img.width, img.height);
    const scale = longest > maxSide ? maxSide / longest : 1;

    // Si ya es chica y liviana, no reprocesar
    if (scale === 1 && file.size <= 700 * 1024 && /^image\/(jpeg|jpg|webp)$/i.test(file.type)) {
        return { file, img, width: img.width, height: img.height };
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const out = await canvasToJpegFile(canvas, 'foto-comprimida.jpg', quality);
    const outImg = await loadImageFromFile(out);
    return { file: out, img: outImg, width: outImg.width, height: outImg.height };
}

/**
 * Exporta lo visible en un viewport circular (cover + pan + zoom) a un JPEG cuadrado.
 * viewportSize: tamaño CSS del círculo.
 * offset: { x, y } desplazamiento del centro de la imagen respecto al centro del viewport.
 * zoom: multiplicador sobre el scale base "cover".
 */
export async function exportCircularCrop({
    image,
    viewportSize,
    offset = { x: 0, y: 0 },
    zoom = 1,
    outputSize = 512,
    quality = 0.85,
    fileName = 'perfil.jpg',
}) {
    const baseScale = Math.max(viewportSize / image.width, viewportSize / image.height);
    const displayScale = baseScale * Math.max(1, zoom);

    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext('2d');

    // Fondo neutro por si hay transparencias
    ctx.fillStyle = '#182850';
    ctx.fillRect(0, 0, outputSize, outputSize);

    const s = outputSize / viewportSize;
    const drawW = image.width * displayScale * s;
    const drawH = image.height * displayScale * s;
    const cx = outputSize / 2 + offset.x * s;
    const cy = outputSize / 2 + offset.y * s;

    ctx.save();
    ctx.beginPath();
    ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(image, cx - drawW / 2, cy - drawH / 2, drawW, drawH);
    ctx.restore();

    const file = await canvasToJpegFile(canvas, fileName, quality);
    const url = URL.createObjectURL(file);
    return { file, url };
}
